import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import DataTable from "../../components/common/DataTable";
import { api_paths } from "../../config/api.paths";
import API from "../../config/axios";
import { formatToUSDate } from "../../utils/dateFormat";
import { toastErrorMessage } from "../../utils/utils";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { SET_LOADING } from "../../redux/user";
import LeftArrow from "../../assets/images/committee/ArrowLeft.svg";
import SelectField from "../../components/common/SelectField";
// 🔹 Status Badge Component
const StatusBadge = ({ status }) => {
  const classes = {
    Active: "badge bg-success-subtle",
    Free: "badge bg-warning-subtle",
    Expired: "badge bg-danger-subtle",
    InActive: "badge bg-danger-subtle",
  };

  return <span className={`${classes[status]} px-3 py-2 rounded-3`}>{status}</span>;
};

export default function ManageMembership() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.USER_DATA);
  const [plans, setPlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [pastMembers, setPastMembers] = useState([]);
  const [notBoughtMembers, setNotBoughtMembers] = useState([]);
  const [isDataReady, setIsDataReady] = useState(false);

  const selectedPlan = useMemo(() => {
    return plans.find((p) => p._id === selectedPlanId) || null;
  }, [plans, selectedPlanId]);

  const isActivePlan = selectedPlan?.is_active || false;

  // 🔹 Fetch API Data
  const fetchInitialData = async () => {
    try {
      setIsDataReady(false);
      dispatch(SET_LOADING(true));
      const planRes = await API.get(api_paths.member.membership.GET_ALL_PLANS);
      const fetchedPlans = planRes.data?.payload?.result || [];
      setPlans(fetchedPlans);

      if (fetchedPlans.length > 0) {
        const activePlan = fetchedPlans.find((p) => p.is_active) || fetchedPlans[0];
        setSelectedPlanId(activePlan._id);
      } else {
        setIsDataReady(true);
        dispatch(SET_LOADING(false));
      }
    } catch (err) {
      console.error("Error loading plans:", err);
      dispatch(SET_LOADING(false));
    }
  };

  const fetchPlanData = async (planId) => {
    try {
      setIsDataReady(false);
      dispatch(SET_LOADING(true));

      const response = await API.get(`${api_paths.member.membership.PAST_MEMBERSHIP_LIST}?plan_id=${planId}`);

      const payload = response.data?.payload?.result;
      setPastMembers(payload?.data || []);
      setNotBoughtMembers(payload?.notBoughtMembers || []);

      setIsDataReady(true);
    } catch (err) {
      console.error("Error fetching plan data:", err);
      setIsDataReady(true);
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (selectedPlanId) {
      fetchPlanData(selectedPlanId);
    }
    // eslint-disable-next-line
  }, [selectedPlanId]);

  // 🔹 Process payment for pending members
  const handleProcessToPay = async () => {
    try {
      if (!selectedPlan?._id) return;

      const primaryIncluded = notBoughtMembers.some((m) => m._id === user?._id);
      if (memberCount === 0 && !primaryIncluded) {
        toastErrorMessage("No pending billable members to process payment for.");
        return;
      }

      dispatch(SET_LOADING(true));

      const familyMemberIds = billablePendingMembers
        .filter((m) => m.relationship !== "Primary")
        .map((m) => m._id);

      const payload = {
        family_members: familyMemberIds,
        payment_type: "membership",
        plan_id: selectedPlan._id,
        amount: totalAmount,
        primary_included: primaryIncluded,
      };

      const response = await API.post(
        `${api_paths.member.payment.PROCESS_TO_PAY_MEMBERSHIP}?type=adding-members`,
        payload
      );

      if (response.data?.payload?.token) {
        const token = response.data?.payload?.token;
        const form = document.createElement("form");
        form.method = "POST";
        form.action = `${process.env.REACT_APP_PAYMENT_GATEWAY_URL}hosted-payments`;

        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "ssl_txn_auth_token";
        input.value = token;

        form.appendChild(input);
        document.body.appendChild(form);
        form.submit();
      } else {
        throw new Error("No payment token returned");
      }
    } catch (err) {
      console.error("Payment error:", err);
      dispatch(SET_LOADING(false));
      toastErrorMessage("Failed to initiate payment. Please try again.");
    }
  };

  // 🔹 Columns and Logic for Active Plan
  const getMembershipStatus = (m) => {
    // If m.status exists, it means the member is in the past-membership list (paid for this year)
    if (m?.status) {
      if (m?.is_free_membership) return "Free";
      return m.status.charAt(0).toUpperCase() + m.status.slice(1);
    }

    // If they are NOT in the past-membership list
    return "InActive";
  };

  const activeRowData = useMemo(() => {
    return pastMembers;
  }, [pastMembers]);

  const baseColumns = [
    {
      key: "name",
      label: "Name",
      render: (_, row) =>
        `${row.first_name} ${row.middle_name || ""} ${row.last_name}`.replace(/\s+/g, " ").trim(),
    },
    {
      key: "date_of_birth",
      label: "Date of Birth",
      render: (val) => (val ? formatToUSDate(val) : "-"),
    },
    {
      key: "gender",
      label: "Gender",
      render: (val) => <span className="text-capitalize">{val}</span>,
    },
    { key: "relationship", label: "Relation", render: (val, row) => val || row.relation || "-" },
    {
      key: "membership_status",
      label: "Membership",
      render: (_, row) => <StatusBadge status={getMembershipStatus(row)} />,
    },
  ];

  const activeColumns = [
    ...baseColumns,
    {
      key: "membership_expiry_date",
      label: "Expiry Date",
      render: (val) => (val ? formatToUSDate(val) : "-"),
    },
  ];

  const pendingColumns = [
    ...baseColumns,
    {
      key: "membership_expiry_date",
      label: "Expired At",
      render: (val, row) =>
        getMembershipStatus(row) === "Expired" && val ? formatToUSDate(val) : "-",
    },
  ];

  const billablePendingMembers = useMemo(
    () => notBoughtMembers.filter((m) => getMembershipStatus(m) !== "Free"),
    [notBoughtMembers]
  );

  const memberCount = billablePendingMembers.length;
  const amount = selectedPlan?.amount || 0;
  const discountAmount = selectedPlan?.discount_amount || 0;
  const discountTillDate = selectedPlan?.discount_till_date || null;
  const todayStart = new Date().setHours(0, 0, 0, 0);
  const discountValid =
    !!discountAmount &&
    !!discountTillDate &&
    new Date(discountTillDate).setHours(0, 0, 0, 0) >= todayStart;

  const effectiveDiscount = discountValid ? discountAmount : 0;
  const totalDiscount = memberCount * effectiveDiscount;
  const totalAmount = memberCount * amount - totalDiscount;

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <img
            className="cursor-pointer"
            src={LeftArrow}
            alt="back-arrow"
            onClick={() => navigate(-1)}
          />
          <h1 className="mb-0 fw-bold text-main f-20 ms-3">
            {selectedPlan?.plan_name || "Membership"}
          </h1>
        </div>
        <div style={{ width: "200px" }}>
          <SelectField
            value={
              selectedPlan
                ? {
                  label: selectedPlan.plan_year || selectedPlan.plan_name,
                  value: selectedPlan._id,
                }
                : null
            }
            options={plans.map((p) => ({ label: p.plan_year || p.plan_name, value: p._id }))}
            onChange={(opt) => setSelectedPlanId(opt.value)}
          />
        </div>
      </div>
      {isDataReady &&
        (isActivePlan ? (
          <>
            <div className="mb-4">
              <DataTable
                columns={activeColumns}
                data={activeRowData}
                emptyMessage="Membership not found"
              />
            </div>

            {notBoughtMembers.length > 0 && (
              <div>
                <h1 className="mb-4 pt-2 fw-bold text-main f-20">Pay InActive Membership fees</h1>
                <DataTable
                  columns={pendingColumns}
                  data={notBoughtMembers}
                  footer={
                    selectedPlan && (
                      <div
                        className="d-flex justify-content-between align-items-center p-3"
                        style={{ backgroundColor: "#FFF7EB", borderTop: "1px solid #efefef" }}
                      >
                        <div>
                          <p className="fw-semibold mb-0">Membership Fees</p>
                          <p className="mb-0">{selectedPlan.plan_name}</p>
                        </div>
                        <div>
                          <p className="fw-semibold mb-0">
                            Total Member(s) {memberCount} × ${amount}
                          </p>
                          {discountValid && (
                            <p className="mb-0">(${totalDiscount} discount applied)</p>
                          )}
                        </div>
                        <div className="d-flex align-items-center gap-3 text-end">
                          <div>
                            {discountValid && (
                              <p className="mb-0">
                                Total Discount:{" "}
                                <span className="fw-semibold ms-1">-${totalDiscount}</span>
                              </p>
                            )}
                            <p className="mb-0">
                              <b>Total Amount: </b>
                              <span className="fw-bold">${totalAmount}</span>
                            </p>
                          </div>
                          <button className="btn btn-orange px-4 ms-3" onClick={handleProcessToPay}>
                            Process to Pay
                          </button>
                        </div>
                      </div>
                    )
                  }
                />
              </div>
            )}
          </>
        ) : (
          <div className="mb-4">
            <DataTable
              columns={activeColumns}
              data={activeRowData}
              emptyMessage="Membership not found"
            />
          </div>
        ))}
    </div>
  );
}
